import { type ComponentPropsWithoutRef, forwardRef } from 'react';

import { cva, type VariantProps } from '../../utils/cva';

const blueGradientBackgroundVariants = cva({
  base: ['bg-cover', 'bg-center', 'bg-no-repeat'],
  variants: {
    size: {
      fit: [],
      fullScreen: ['min-h-screen', 'w-full'],
    },
  },
  defaultVariants: {
    size: 'fullScreen',
  },
});

/**
 * SVG encoded as a data URI for use as background-image.
 * Two blurred circles create a soft ambient gradient:
 * - Bottom-right: larger circle (r=551)
 * - Top-left: smaller circle (r=317.5)
 * Both use color #A3BAFF with heavy gaussian blur (stdDeviation=300)
 */
const BLUE_GRADIENT_SVG_DATA_URI = `data:image/svg+xml,${encodeURIComponent(`<svg width="1440" height="900" viewBox="0 0 1440 900" fill="none" xmlns="http://www.w3.org/2000/svg"><g filter="url(#filter0_f_385_4626)"><circle cx="1287" cy="1206.5" r="551" fill="#A3BAFF"/></g><g filter="url(#filter1_f_385_4626)"><circle cx="69.5" cy="84" r="317.5" fill="#A3BAFF"/></g><defs><filter id="filter0_f_385_4626" x="136" y="55.5" width="2302" height="2302" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="300" result="effect1_foregroundBlur_385_4626"/></filter><filter id="filter1_f_385_4626" x="-848" y="-833.5" width="1835" height="1835" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="300" result="effect1_foregroundBlur_385_4626"/></filter></defs></svg>`)}`;

export type BlueGradientBackgroundProps = ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof blueGradientBackgroundVariants> & {
    'data-testid'?: string;
  };

/**
 * A decorative background component with a soft blue gradient effect.
 * Uses two blurred circles positioned at opposite corners to create
 * an ambient gradient appearance via CSS background-image.
 *
 * @example
 * ```tsx
 * <BlueGradientBackground>
 *   <YourContent />
 * </BlueGradientBackground>
 * ```
 */
export const BlueGradientBackground = forwardRef<
  HTMLDivElement,
  BlueGradientBackgroundProps
>((props, ref) => {
  const {
    className,
    size,
    children,
    style,
    'data-testid': dataTestId = 'blue-gradient-background',
    ...rest
  } = props;

  return (
    <div
      ref={ref}
      data-testid={dataTestId}
      className={blueGradientBackgroundVariants({ size, className })}
      style={{
        backgroundImage: `url("${BLUE_GRADIENT_SVG_DATA_URI}")`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
});
